import { toMoney } from '@utils/Format'

const DEFAULT_COLOR = '#22C55E'
const iconCache = new Map()
const ICON_ERROR_TTL = 60 * 1000
const TOOLTIP_DEFAULTS = {
  enabled: true,
  offset: 8,
  padding: 8,
  maxWidth: 240,
  radius: 8,
  titleFont: '600 11px "Proxima Nova", ProximaNova, Inter, sans-serif',
  bodyFont: '400 10px "Proxima Nova", ProximaNova, Inter, sans-serif',
  titleLineHeight: 14,
  bodyLineHeight: 12,
  backgroundColor: '#ffffff',
  borderColor: '#e5e7eb',
  textColor: '#1f2937',
  shadowColor: 'rgba(15, 23, 42, 0.15)',
  shadowBlur: 16,
}

const isRemoteIcon = (value) => {
  if (!value || typeof value !== 'string') {
    return false
  }
  return /^https?:\/\//i.test(value.trim())
}

const colorWithAlpha = (color, alpha = 0.18) => {
  if (!color || typeof color !== 'string') {
    return `rgba(34, 197, 94, ${alpha})`
  }

  const normalized = color.replace('#', '').trim()
  if (/^[0-9a-f]{3,8}$/i.test(normalized)) {
    const hex =
      normalized.length === 3
        ? normalized
            .split('')
            .map((char) => char + char)
            .join('')
        : normalized.padEnd(6, '0')

    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  const rgbMatch = color.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return `rgba(34, 197, 94, ${alpha})`
}

const withAlpha = (color, alpha = 1) => {
  if (!color || typeof color !== 'string') {
    return colorWithAlpha(DEFAULT_COLOR, alpha)
  }

  const normalized = color.replace('#', '').trim()
  if (/^[0-9a-f]{3,8}$/i.test(normalized)) {
    const hex =
      normalized.length === 3
        ? normalized
            .split('')
            .map((char) => char + char)
            .join('')
        : normalized.padEnd(6, '0')

    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  const rgbMatch = color.match(/^rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return colorWithAlpha(DEFAULT_COLOR, alpha)
}

const loadIcon = (icon, onResolve) => {
  if (!isRemoteIcon(icon) || typeof Image === 'undefined') {
    return null
  }

  let entry = iconCache.get(icon)
  if (entry && entry.status === 'error') {
    const shouldRetry =
      !entry.errorAt || Date.now() - entry.errorAt > ICON_ERROR_TTL
    if (shouldRetry) {
      iconCache.delete(icon)
      entry = null
    } else {
      return null
    }
  }

  if (!entry) {
    const image = new Image()
    entry = {
      image,
      status: 'loading',
      callbacks: [],
    }

    image.crossOrigin = 'anonymous'
    image.onload = () => {
      entry.status = 'loaded'
      entry.callbacks.forEach((callback) => {
        if (typeof callback === 'function') {
          callback()
        }
      })
      entry.callbacks = []
    }
    image.onerror = () => {
      entry.status = 'error'
      entry.errorAt = Date.now()
      entry.callbacks = []
    }
    image.src = icon

    iconCache.set(icon, entry)
  }

  if (typeof onResolve === 'function' && entry.status === 'loading') {
    if (!entry.callbacks.includes(onResolve)) {
      entry.callbacks.push(onResolve)
    }
  }

  return entry
}

const ANCHORABLE_DATASET_TYPES = new Set(['line', 'bar'])

const getDatasetForGoals = (chart) => {
  const totalDatasets = chart.data.datasets || []
  for (let index = 0; index < totalDatasets.length; index += 1) {
    const dataset = totalDatasets[index]
    if (!dataset || !ANCHORABLE_DATASET_TYPES.has(dataset.type)) {
      continue
    }

    const meta = chart.getDatasetMeta(index)
    if (
      !meta ||
      !Array.isArray(meta.data) ||
      !meta.data.length ||
      meta.hidden
    ) {
      continue
    }

    return meta
  }

  return null
}

const clamp = (value, min, max) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return min
  }
  if (numeric < min) {
    return min
  }
  if (numeric > max) {
    return max
  }
  return numeric
}

export const computeGoalStackCenters = ({
  baseY,
  count,
  radius,
  lineHeight,
  chartArea,
  gap = 2,
} = {}) => {
  const total = Number(count) || 0
  if (total <= 0) {
    return []
  }

  const adjustedRadius = Math.max(Number(radius) || 12, 8)
  const adjustedLine = Math.max(Number(lineHeight) || 6, 4)
  const area = chartArea || { top: 0, bottom: 0 }
  const minY = area.top + adjustedRadius + adjustedLine + 2
  const maxY = area.bottom - adjustedRadius - 2

  const step = adjustedRadius * 2 + adjustedLine + gap
  const topCenterDesired =
    baseY - (adjustedRadius + adjustedLine) - step * (total - 1)
  const bottomCenterDesired = baseY - (adjustedRadius + adjustedLine)

  let shift = 0
  if (topCenterDesired < minY) {
    shift = minY - topCenterDesired
  }
  if (bottomCenterDesired + shift > maxY) {
    shift = maxY - bottomCenterDesired
  }

  const topCenter = topCenterDesired + shift
  return Array.from({ length: total }, (_, index) => topCenter + index * step)
}

export const getLineEndY = (baseY, circleCenterY, adjustedRadius) => {
  const radius = Number(adjustedRadius) || 0
  const isCircleAbove = circleCenterY < baseY
  return circleCenterY + (isCircleAbove ? radius : -radius)
}

export const clearChartTooltip = (chart) => {
  if (!chart || !chart.tooltip) {
    return
  }

  const tooltip = chart.tooltip
  if (Array.isArray(tooltip._active) && tooltip._active.length) {
    tooltip._active = []
    if (typeof tooltip.update === 'function') {
      tooltip.update()
    }
    if (typeof chart.draw === 'function') {
      chart.draw()
    }
  }
}

export const buildRemainingLine = (remaining, formatValue) => {
  if (remaining > 0) return `Faltam: ${formatValue(remaining, 0)}`
  if (remaining < 0) return `Excedente: ${formatValue(Math.abs(remaining), 0)}`
  return null
}

export const buildGoalValueLines = (raw, formatValue) => {
  const lines = []
  if (raw.valor_objetivo === undefined || raw.valor_objetivo === null) {
    return lines
  }
  const target = Number(raw.valor_objetivo) || 0
  lines.push(`Meta: ${formatValue(target, 0)}`)

  const current = Number(raw.valor_atual) || 0
  if (current <= 0 && target <= 0) return lines

  lines.push(`Atual: ${formatValue(current, 0)}`)
  const remainingLine = buildRemainingLine(target - current, formatValue)
  if (remainingLine) lines.push(remainingLine)

  if (target > 0) {
    const pct = Math.min(Math.round((current / target) * 100), 999)
    lines.push(`Progresso: ${pct}%`)
  }
  return lines
}

export const buildGoalTooltipContent = (goal, formatValue = toMoney) => {
  if (!goal) return null

  const raw = goal.raw || {}
  const title =
    goal.name || (typeof raw.name === 'string' ? raw.name : '') || 'Objetivo'
  const lines = buildGoalValueLines(raw, formatValue)

  if (raw.data_fim) lines.push(`Prazo: ${raw.data_fim}`)
  lines.push('', 'Clique para ver detalhes')

  return { title, lines }
}

const getEventPosition = (chart, event) => {
  if (!event) {
    return null
  }

  if (Number.isFinite(event.x) && Number.isFinite(event.y)) {
    return { x: event.x, y: event.y }
  }

  const nativeEvent = event.native || event
  if (
    nativeEvent &&
    Number.isFinite(nativeEvent.offsetX) &&
    Number.isFinite(nativeEvent.offsetY)
  ) {
    return { x: nativeEvent.offsetX, y: nativeEvent.offsetY }
  }

  if (
    nativeEvent &&
    Number.isFinite(nativeEvent.clientX) &&
    Number.isFinite(nativeEvent.clientY) &&
    chart &&
    chart.canvas
  ) {
    const rect = chart.canvas.getBoundingClientRect()
    return {
      x: nativeEvent.clientX - rect.left,
      y: nativeEvent.clientY - rect.top,
    }
  }

  return null
}

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  const safeRadius = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + safeRadius, y)
  ctx.lineTo(x + width - safeRadius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius)
  ctx.lineTo(x + width, y + height - safeRadius)
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - safeRadius,
    y + height
  )
  ctx.lineTo(x + safeRadius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius)
  ctx.lineTo(x, y + safeRadius)
  ctx.quadraticCurveTo(x, y, x + safeRadius, y)
  ctx.closePath()
}

// Glifo curto (ex: '↑', '$') desenhado dentro da bolha quando não há icon por
// URL — permite marcadores tipados sem acoplar ao name.
const normalizeGoalSymbol = (symbol) =>
  (typeof symbol === 'string' && symbol.trim()) || null

export const normalizeGoals = (goals) => {
  if (!Array.isArray(goals)) {
    return []
  }

  return goals
    .map((goal) => {
      if (!goal || typeof goal !== 'object') {
        return null
      }

      const index =
        goal.index !== undefined && goal.index !== null
          ? String(goal.index)
          : null
      if (!index) {
        return null
      }

      return {
        index,
        color:
          typeof goal.color === 'string' && goal.color
            ? goal.color
            : DEFAULT_COLOR,
        icon: goal.icon || null,
        symbol: normalizeGoalSymbol(goal.symbol),
        name:
          (typeof goal.name === 'string' && goal.name.trim()) ||
          (goal.raw && goal.raw.name) ||
          '',
        raw: goal.raw || goal,
      }
    })
    .filter(Boolean)
}

export const drawGoalConnectorLines = (ctx, x, baseY, sortedCenters, adjustedRadius, baseColor, hasHover) => {
  const alpha = hasHover ? 0.25 : 0.45
  const bottomCenter = sortedCenters[sortedCenters.length - 1]
  const baseLineEnd = getLineEndY(baseY, bottomCenter, adjustedRadius)
  ctx.save()
  ctx.strokeStyle = withAlpha(baseColor, alpha)
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x, baseY)
  ctx.lineTo(x, baseLineEnd)
  ctx.stroke()
  ctx.restore()

  sortedCenters.forEach((center, index) => {
    if (index === 0) return
    const prevCenter = sortedCenters[index - 1]
    const prevEdge = prevCenter + adjustedRadius
    const currentEdge = center - adjustedRadius
    if (currentEdge <= prevEdge) return
    ctx.save()
    ctx.strokeStyle = withAlpha(baseColor, alpha)
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(x, prevEdge)
    ctx.lineTo(x, currentEdge)
    ctx.stroke()
    ctx.restore()
  })
}

const drawGoalCircle = (ctx, x, circleCenterY, adjustedRadius, goal, fadeAlpha) => {
  ctx.save()
  ctx.fillStyle = colorWithAlpha(goal.color, 0.18 * fadeAlpha)
  ctx.strokeStyle = withAlpha(goal.color || DEFAULT_COLOR, fadeAlpha)
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(x, circleCenterY, adjustedRadius, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.restore()
}

export const drawGoalIcon = (ctx, { x, circleCenterY, adjustedRadius, goal, font, fadeAlpha, chart }) => {
  const iconSize = adjustedRadius * 1.1
  const entry = loadIcon(goal.icon, () => chart.draw())

  if (entry && entry.status === 'loaded') {
    ctx.save()
    ctx.beginPath()
    ctx.arc(x, circleCenterY, adjustedRadius - 1, 0, Math.PI * 2)
    ctx.clip()
    ctx.drawImage(entry.image, x - iconSize / 2, circleCenterY - iconSize / 2, iconSize, iconSize)
    ctx.restore()
  } else {
    const fallback =
      goal.symbol ||
      (goal.name && typeof goal.name === 'string' ? goal.name.charAt(0).toUpperCase() : '•')
    ctx.save()
    ctx.fillStyle = withAlpha(goal.color || DEFAULT_COLOR, fadeAlpha)
    ctx.font = font
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(fallback, x, circleCenterY)
    ctx.restore()
  }
}

const resolveTooltipContent = (hoveredMarker, tooltipOptions) => {
  if (!hoveredMarker || !Array.isArray(hoveredMarker.goals) || !hoveredMarker.goals.length) {
    return null
  }
  const goal = hoveredMarker.goals[0]
  return typeof tooltipOptions.getContent === 'function'
    ? tooltipOptions.getContent(goal)
    : buildGoalTooltipContent(goal)
}

const measureTooltipBox = (ctx, title, lines, tooltipOptions) => {
  let maxTextWidth = 0
  if (title) {
    ctx.font = tooltipOptions.titleFont
    maxTextWidth = Math.max(maxTextWidth, ctx.measureText(title).width)
  }
  if (lines.length) {
    ctx.font = tooltipOptions.bodyFont
    lines.forEach((line) => {
      maxTextWidth = Math.max(maxTextWidth, ctx.measureText(line).width)
    })
  }
  const textPadding = tooltipOptions.padding
  const boxWidth = Math.min(tooltipOptions.maxWidth, maxTextWidth + textPadding * 2)
  const titleHeight = title ? tooltipOptions.titleLineHeight : 0
  const bodyHeight = lines.length ? lines.length * tooltipOptions.bodyLineHeight : 0
  const lineGap = title && lines.length ? 4 : 0
  const boxHeight = textPadding * 2 + titleHeight + bodyHeight + lineGap
  return { boxWidth, boxHeight, titleHeight, lineGap }
}

const resolveTooltipPosition = (chart, hoveredMarker, tooltipOptions, boxWidth, boxHeight, radius) => {
  const markerX = hoveredMarker.x
  const markerY = hoveredMarker.centerY || hoveredMarker.y
  const markerRadius = hoveredMarker.radius || radius
  const { chartArea } = chart
  const boxX = clamp(markerX - boxWidth / 2, chartArea.left + 4, chartArea.right - boxWidth - 4)
  let boxY = markerY - markerRadius - tooltipOptions.offset - boxHeight
  if (boxY < chartArea.top + 4) {
    boxY = markerY + markerRadius + tooltipOptions.offset
  }
  return { boxX, boxY }
}

const paintTooltipBackground = (ctx, boxX, boxY, boxWidth, boxHeight, tooltipOptions) => {
  ctx.save()
  drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, tooltipOptions.radius)
  ctx.fillStyle = tooltipOptions.backgroundColor
  ctx.shadowColor = tooltipOptions.shadowColor
  ctx.shadowBlur = tooltipOptions.shadowBlur
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.shadowColor = 'transparent'
  ctx.strokeStyle = tooltipOptions.borderColor
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

const paintTooltipText = (ctx, position, content, tooltipOptions) => {
  const { boxX, boxY, boxWidth, titleHeight, lineGap } = position
  const { title, lines } = content
  const textPadding = tooltipOptions.padding
  ctx.save()
  ctx.fillStyle = tooltipOptions.textColor
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  let textY = boxY + textPadding
  const textX = boxX + textPadding
  const maxTextWidth = boxWidth - textPadding * 2
  if (title) {
    ctx.font = tooltipOptions.titleFont
    ctx.fillText(title, textX, textY, maxTextWidth)
    textY += titleHeight
  }
  if (lines.length) {
    textY += lineGap
    ctx.font = tooltipOptions.bodyFont
    lines.forEach((line) => {
      ctx.fillText(line, textX, textY, maxTextWidth)
      textY += tooltipOptions.bodyLineHeight
    })
  }
  ctx.restore()
}

const drawHoveredTooltip = (ctx, chart, hoveredMarker, tooltipOptions, radius) => {
  const content = resolveTooltipContent(hoveredMarker, tooltipOptions)
  if (!content) return

  const title = content.title || ''
  const lines = Array.isArray(content.lines) ? content.lines : []

  ctx.save()
  const { boxWidth, boxHeight, titleHeight, lineGap } = measureTooltipBox(ctx, title, lines, tooltipOptions)
  ctx.restore()

  const { boxX, boxY } = resolveTooltipPosition(chart, hoveredMarker, tooltipOptions, boxWidth, boxHeight, radius)

  paintTooltipBackground(ctx, boxX, boxY, boxWidth, boxHeight, tooltipOptions)
  paintTooltipText(
    ctx,
    { boxX, boxY, boxWidth, titleHeight, lineGap },
    { title, lines },
    tooltipOptions
  )
}

export const createGoalMarkersPlugin = ({
  goals = [],
  radius = 14,
  lineHeight = 8,
  font = '600 10px "Proxima Nova", ProximaNova, Inter, sans-serif',
  onUpdate = null,
  onHover = null,
  onClick = null,
  tooltip = null,
  suppressChartTooltip = true,
} = {}) => {
  const normalizedGoals = normalizeGoals(goals)
  const tooltipOptions = {
    ...TOOLTIP_DEFAULTS,
    ...(tooltip === true ? { enabled: true } : tooltip || {}),
  }
  const tooltipEnabled = !!tooltipOptions.enabled
  let currentMarkers = []
  let hoveredMarker = null

  if (!normalizedGoals.length) {
    return {}
  }

  const handleMouseOut = (chart) => {
    if (!hoveredMarker) return
    hoveredMarker = null
    if (typeof onHover === 'function') onHover(null)
    if (chart.canvas) chart.canvas.style.cursor = 'default'
    if (tooltipEnabled) chart.draw()
  }

  const findHitMarker = (position) => currentMarkers.find((marker) => {
    const markerRadius = marker.radius || radius
    const dx = position.x - marker.x
    const dy = position.y - (marker.centerY || marker.y)
    return dx * dx + dy * dy <= markerRadius * markerRadius
  })

  const handleMarkerClick = (type, hitMarker) => {
    if (type !== 'click' || !hitMarker || typeof onClick !== 'function') return
    const clickedGoal = hitMarker.goals && hitMarker.goals[0]
    if (clickedGoal) onClick(clickedGoal)
  }

  const updateHoveredMarker = (chart, hitMarker) => {
    if (hitMarker === hoveredMarker) return
    hoveredMarker = hitMarker || null
    if (typeof onHover === 'function') onHover(hoveredMarker)
    if (!tooltipEnabled) return
    if (hoveredMarker && suppressChartTooltip) clearChartTooltip(chart)
    chart.draw()
  }

  return {
    afterEvent(chart, event) {
      if (!currentMarkers.length || (!tooltipEnabled && !onHover && !onClick)) return

      const type = event && event.type
      if (type === 'mouseout') {
        handleMouseOut(chart)
        return
      }

      const position = getEventPosition(chart, event)
      if (!position) return

      const hitMarker = findHitMarker(position)

      if (chart.canvas) chart.canvas.style.cursor = hitMarker ? 'pointer' : 'default'
      handleMarkerClick(type, hitMarker)
      updateHoveredMarker(chart, hitMarker)
    },
    afterDatasetsDraw(chart) {
      const meta = getDatasetForGoals(chart)
      if (!meta) return

      const labels = chart.data.labels || []
      const { ctx, chartArea } = chart
      const markersForUpdate = []
      const adjustedRadius = Math.max(Number(radius) || 12, 8)
      const adjustedLine = Math.max(Number(lineHeight) || 6, 4)

      const goalsByIndex = new Map()
      normalizedGoals.forEach((goal) => {
        const labelIndex = labels.findIndex((label) => String(label) === goal.index)
        if (labelIndex === -1) return
        if (!goalsByIndex.has(labelIndex)) goalsByIndex.set(labelIndex, [])
        goalsByIndex.get(labelIndex).push(goal)
      })

      const hasHover = !!hoveredMarker
      goalsByIndex.forEach((goalsForIndex, labelIndex) => {
        const element = meta.data[labelIndex]
        const model = element && (element._model || element._view)
        if (!model) return

        const x = model.x
        const baseY = model.y
        const centers = computeGoalStackCenters({ baseY, count: goalsForIndex.length, radius: adjustedRadius, lineHeight: adjustedLine, chartArea })

        if (centers.length) {
          const baseColor = (goalsForIndex[0] && goalsForIndex[0].color) || DEFAULT_COLOR
          const sortedCenters = [...centers].sort((a, b) => a - b)
          drawGoalConnectorLines(ctx, x, baseY, sortedCenters, adjustedRadius, baseColor, hasHover)
        }

        goalsForIndex.forEach((goal, stackIndex) => {
          const circleCenterY = centers[stackIndex] === undefined
            ? clamp(baseY - (adjustedRadius + adjustedLine), chartArea.top + adjustedRadius + adjustedLine + 2, chartArea.bottom - adjustedRadius - 2)
            : centers[stackIndex]
          const isHovered = hoveredMarker && Array.isArray(hoveredMarker.goals) && hoveredMarker.goals.length && hoveredMarker.goals[0] === goal
          let fadeAlpha = 0.45
          if (hasHover) {
            fadeAlpha = isHovered ? 1 : 0.25
          }

          drawGoalCircle(ctx, x, circleCenterY, adjustedRadius, goal, fadeAlpha)
          drawGoalIcon(ctx, { x, circleCenterY, adjustedRadius, goal, font, fadeAlpha, chart })

          markersForUpdate.push({
            x,
            y: baseY,
            centerY: circleCenterY,
            radius: adjustedRadius,
            fillColor: colorWithAlpha(goal.color, 0.18),
            color: goal.color || DEFAULT_COLOR,
            goals: [goal],
            stackIndex,
            stackSize: goalsForIndex.length,
          })
        })
      })

      currentMarkers = markersForUpdate

      if (tooltipEnabled) {
        drawHoveredTooltip(ctx, chart, hoveredMarker, tooltipOptions, radius)
      }

      if (typeof onUpdate === 'function') {
        onUpdate({ markers: markersForUpdate, size: { width: chart.width, height: chart.height } })
      }
    },
  }
}

export default createGoalMarkersPlugin
